import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import campaignApi from '../api/campaigns.api';
import { Contact } from '../types/campaign.types';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import AddContactForm from '../components/campaigns/AddContactForm';

interface ContactsPageProps {}

const ContactsPage: React.FC<ContactsPageProps> = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const pageSize = 50;  // Fixed page size
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filtering
    const [validationFilter, setValidationFilter] = useState<'all' | 'valid' | 'invalid'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);

    const campaignIdNum = parseInt(campaignId || '0', 10);

    // Fetch contacts
    const fetchContacts = useCallback(async (pageNum: number = 1) => {
        if (!campaignIdNum) return;

        setLoading(true);
        try {
            const response = await campaignApi.getContacts({
                campaignId: campaignIdNum,
                page: pageNum,
                page_size: pageSize,
                validation_filter: validationFilter !== 'all' ? validationFilter : undefined,
                search: searchTerm || undefined,
            });

            setContacts(response.items);
            setTotal(response.total);
            setTotalPages(response.total_pages);
            setPage(pageNum);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load contacts';
            showToast(message, 'error');
        } finally {
            setLoading(false);
        }
    }, [campaignIdNum, validationFilter, searchTerm, pageSize, showToast]);

    useEffect(() => {
        fetchContacts(1);
    }, [fetchContacts]);

    const handleAddContact = async () => {
        await fetchContacts(1);
        setShowAddModal(false);
        setEditingContact(null);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setPage(1);
    };

    if (!campaignIdNum) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-gray-600 mb-4">Invalid campaign ID</p>
                    <Button onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
                </div>
            </div>
        );
    }

    const validCount = contacts.filter(c => c.is_valid).length;
    const invalidCount = contacts.filter(c => !c.is_valid).length;

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/campaigns/${campaignIdNum}`)}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        ← Back
                    </button>
                    <h1 className="text-3xl font-bold">Campaign Contacts</h1>
                </div>
                <Button onClick={() => setShowAddModal(true)} className="bg-green-600">
                    + Add Contact
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-gray-600 text-sm">Total Contacts</p>
                    <p className="text-2xl font-bold">{total}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-gray-600 text-sm">Valid</p>
                    <p className="text-2xl font-bold text-green-600">{validCount}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-gray-600 text-sm">Invalid</p>
                    <p className="text-2xl font-bold text-red-600">{invalidCount}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                        placeholder="Search by name or phone..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        type="text"
                    />
                    <select
                        value={validationFilter}
                        onChange={(e) => setValidationFilter(e.target.value as any)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Contacts</option>
                        <option value="valid">Valid Only</option>
                        <option value="invalid">Invalid Only</option>
                    </select>
                    <Button onClick={() => fetchContacts(page)}>Refresh</Button>
                </div>
            </div>

            {/* Contacts Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <LoadingSpinner />
                ) : contacts.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <p>No contacts found</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Phone
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Scheduled Time
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">
                                            Added
                                        </th>
                                        <th className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {contacts.map((contact) => (
                                        <tr key={contact.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {contact.name || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-mono text-gray-700">
                                                {contact.phone_number}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-700">
                                                {contact.email || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-700">
                                                {contact.scheduled_time && contact.scheduled_time.length > 0
                                                    ? contact.scheduled_time.join(', ')
                                                    : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                {contact.is_valid ? (
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                        ✓ Valid
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800"
                                                        title={contact.validation_error || 'Invalid contact'}
                                                    >
                                                        ✗ Invalid
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {new Date(contact.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-right text-sm">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setEditingContact(contact);
                                                        setShowAddModal(true);
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="px-6 py-4 border-t flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing {(page - 1) * pageSize + 1} to{' '}
                                {Math.min(page * pageSize, total)} of {total}
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={() => fetchContacts(page - 1)}
                                    disabled={page === 1}
                                    className="disabled:opacity-50"
                                >
                                    Previous
                                </Button>
                                <span className="px-4 py-2 text-sm text-gray-700">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    onClick={() => fetchContacts(page + 1)}
                                    disabled={page >= totalPages}
                                    className="disabled:opacity-50"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Add Contact Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setEditingContact(null);
                }}
                title={editingContact ? 'Edit Contact' : 'Add New Contact'}
                size="lg"
            >
                <AddContactForm
                    campaignId={campaignIdNum}
                    onSuccess={handleAddContact}
                    onCancel={() => {
                        setShowAddModal(false);
                        setEditingContact(null);
                    }}
                    contact={editingContact || undefined}
                />
            </Modal>
        </div>
    );
};

export default ContactsPage;
